import React, { FunctionComponent, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import InputField from "components/textField/InputField";
import Button from "components/button/Button";
import Styles from "views/register/Register.module.scss";
import LoginLeftSection from "components/loginLeftSection/LoginLeftSection";
import { validateEmail, validateName } from "helpers/utils";
import { trackFormInteraction, trackEvent, ClarityEvents } from "utils/clarity";

interface RegisterProps {
  isLoading: boolean;
}

const Register: FunctionComponent<RegisterProps> = (props: RegisterProps) => {
  const { isLoading } = props;
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [requiredField, setRequiredField] = useState<string[]>([]);
  const userObj: any = { name: "", email: "", password: "", profile_pic: null };
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    trackFormInteraction("registration", "opened");
  }, []);

  const handleSignUp = () => {
    const errors: string[] = [];
    if (name.length === 0) errors.push("Name");
    if (password.length === 0) errors.push(" Password");
    if (email.length === 0) errors.push(" Email");
    if (errors.length > 0) {
      setRequiredField(errors);
      trackFormInteraction(
        "registration",
        "validation_error",
        errors.join(",")
      );
      return;
    } else {
      setRequiredField([]);
    }

    if (validateName(name)) {
      setNameError(false);
    } else {
      setNameError(true);
      trackFormInteraction("registration", "validation_error", "invalid_name");
      return;
    }

    if (validateEmail(email)) {
      setEmailError(false);
    } else {
      setEmailError(true);
      trackFormInteraction("registration", "validation_error", "invalid_email");
      return;
    }

    userObj.name = name;
    userObj.email = email;
    userObj.password = password;
    if (location.search !== "") {
      const token = location.search.split("=");
      userObj.token = token[1];
    }
    localStorage.setItem("userObj", JSON.stringify(userObj));
    trackEvent(ClarityEvents.REGISTRATION_STARTED, {
      email_length: email.length,
    });
    setRequiredField([]);
    history.push("/registration/step-two");
  };

  return (
    <div className={Styles.customContainer}>
      <div className={Styles.leftSectionWrapper}>
        <LoginLeftSection />
      </div>
      <div className={Styles.rightSectionWrapper}>
        <div className={Styles.loginArea}>
          <div className={Styles.formContent}>
            <h2 className={Styles.pageTitle}>Create Account</h2>
            <div className={Styles.pageSubtitle}>
              Create a free profile in 3 easy steps to get started.
            </div>

            <div className={Styles.stepBlock}>
              <div className={Styles.stepLabel}>STEP 1</div>
              <div className={Styles.stepSubtitle}>
                Please provide your details
              </div>
            </div>

            <div className={Styles.formWrapper}>
              <div>
                <InputField
                  cssClass={Styles.registerField}
                  handleChange={setName}
                  type="text"
                  placeholder="Full Name"
                />
                {nameError && (
                  <p className={Styles.error}>
                    Name should only contain letters
                  </p>
                )}
              </div>
              <div>
                <InputField
                  cssClass={Styles.registerField}
                  handleChange={setEmail}
                  type="email"
                  placeholder="Email Address"
                />
                {emailError && <p className={Styles.error}>Invalid Email</p>}
              </div>
              <InputField
                cssClass={Styles.registerField}
                handleChange={setPassword}
                type="password"
                placeholder="Password"
              />
              {requiredField.length > 0 && (
                <p
                  className={Styles.error}
                >{`Please enter ${requiredField}`}</p>
              )}
            </div>

            <div className={Styles.termsText}>
              By signing up, you agree to our{" "}
              <a
                href="https://soundbirdapp.com/terms.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent(ClarityEvents.EXTERNAL_LINK, { url: "terms" })
                }
              >
                Terms of Services
              </a>{" "}
              and{" "}
              <a
                href="https://soundbirdapp.com/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent(ClarityEvents.EXTERNAL_LINK, { url: "privacy" })
                }
              >
                Privacy Policy
              </a>
            </div>

            <div className={Styles.registerBtnBlock}>
              <Button
                showLoading={isLoading}
                clickHandler={handleSignUp}
                label="SIGN UP"
                cssClass={Styles.authPrimaryBtn}
              />
            </div>

            <div className={Styles.bottomText}>
              Already have an account? <Link to="/login">LOG IN</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
